/**
 * The builder's canvas: one window onto a grid of terrain, and everything that happens on it.
 *
 * This is the piece both editors share — the admin drawing a curriculum island and a member
 * arranging their own — and what it holds is precisely the part that is subtle: the stroke machine,
 * the two-finger gesture arbitration, the camera, and the blit. Palettes, inspectors, chrome and
 * copy stay with whoever is using it, because those are where the two editors genuinely differ.
 *
 * **It draws with the game's own renderer.** `renderGroundLayer` and `applyShoreline` paint the
 * ground exactly as the map does, so what is on this canvas is what somebody will walk on rather
 * than a preview of it. That decision is the reason this component exists rather than two.
 *
 * Four invariants live in here and every one of them is a bug that was found the hard way:
 *
 *   1. A stroke carries its own `Uint8Array`. Two pointer events can land before React re-renders,
 *      so reading the `tiles` prop for the second one would paint over a copy that never saw the
 *      first.
 *   2. `onPaint` is never called from inside a state updater. It writes to a store, and a store
 *      write during render is a setState-in-render that React drops with a warning.
 *   3. The `tiles` prop is not adopted mid-stroke, or the tile under the cursor is pulled out from
 *      under the brush.
 *   4. Undo is refused while a stroke is live — it would be silently painted back over by the next
 *      pointermove *and* leave a fresh entry on the stack. That is why `onUndo` is called by this
 *      component only after it has cleared its own stroke.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Terrain } from '../../../core/terrain';
import { biomeFor } from '../../art/palette';
import { renderGroundLayer } from '../../game/tiles';
import { TILE, applyShoreline } from '../../game/world';
import { fillFrom, fingerprint, paintLine, previewWorld, rectBetween } from './paint';
import {
  ZOOM_MAX,
  ZOOM_MIN,
  backingScale,
  bufferPointAt,
  cameraPutting,
  clampCamera,
  tileAtPointer,
  viewTransform,
  type Camera,
} from './viewport';

export type BuilderTool = 'cetka' | 'pravougaonik' | 'kanta' | 'kapaljka' | 'objekti' | 'gumica';

export interface Tile {
  x: number;
  y: number;
}

/**
 * What the canvas is allowed to do to the things standing on the terrain.
 *
 * Generic in the reference type so neither editor has to agree with the other about what an object
 * is: the admin's are markers, people and decorations; a member's are decorations and nothing else.
 */
export interface CanvasObjects<Ref> {
  hitTest(x: number, y: number): Ref | null;
  /** `coalesce` is false for the first step of a drag and true for every one after it. */
  move(ref: Ref, x: number, y: number, coalesce: boolean): void;
  /** Take it off. False when refused — the caller has already said why. */
  remove(ref: Ref): boolean;
  /** Put down whatever is in the hand. False when refused. */
  place(x: number, y: number): boolean;
  /** True while something is in the hand, so a tap places instead of selecting. */
  armed: boolean;
}

export interface BuilderCanvasProps<Ref> {
  width: number;
  height: number;
  /** The terrain, owned by the parent. Never written here — see `onPaint`. */
  tiles: Uint8Array;
  biome: string | undefined;
  label: string;

  tool: BuilderTool;
  terrain: Terrain;
  brush: number;
  grid: boolean;
  zoom: number;
  onZoom(next: number): void;

  /** Painted tiles, ready to store. `coalesce` continues the current undo entry. */
  onPaint(next: Uint8Array, coalesce: boolean): void;
  /** The dropper took a terrain off the canvas. */
  onSample(terrain: Terrain): void;

  objects: CanvasObjects<Ref>;
  /** A tap on empty ground, or on a thing — the parent owns the selection. */
  onPick(at: Tile | null): void;
  /** Where the pointer is, for a ghost. Null when it has left. */
  onHover(at: Tile | null): void;
  /** Take back the stroke that was in flight. Called only once this canvas has dropped it. */
  onUndo(): void;
  /**
   * Whether a stroke is live.
   *
   * The parent needs this for one reason: its own undo button. A second pointer landing on the
   * CANVAS aborts the stroke, but a finger on the toolbar is not on the canvas, and an undo landing
   * mid-stroke is painted straight back over by the next pointermove.
   */
  onStroke?(live: boolean): void;

  /** Everything that is not ground, drawn at 1:1 into the buffer. */
  overlay(ctx: CanvasRenderingContext2D): void;
}

type StrokeState<Ref> =
  | { mode: 'paint'; committed: boolean; last: Tile; tiles: Uint8Array }
  | { mode: 'rect'; from: Tile; to: Tile; base: Uint8Array }
  | { mode: 'drag'; target: Ref; last: Tile; moved: boolean };

/**
 * A two-finger gesture, snapshotted when the second finger landed.
 *
 * Everything is measured from this snapshot rather than accumulated between frames, so the view
 * cannot drift over a long pinch and letting go of the zoom while still dragging simply pans.
 */
interface PinchState {
  startDistance: number;
  startZoom: number;
  held: { x: number; y: number };
}


export function BuilderCanvas<Ref>({
  width,
  height,
  tiles,
  biome,
  label,
  tool,
  terrain,
  brush,
  grid,
  zoom,
  onZoom,
  onPaint,
  onSample,
  objects,
  onPick,
  onHover,
  onUndo,
  onStroke,
  overlay,
}: BuilderCanvasProps<Ref>) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bufferRef = useRef<HTMLCanvasElement | null>(null);
  const groundRef = useRef<{ key: string; canvas: HTMLCanvasElement } | null>(null);
  const frameRef = useRef(0);
  const strokeRef = useRef<StrokeState<Ref> | null>(null);
  /** Every pointer currently down. Two of them is the camera, never the drawing. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<PinchState | null>(null);

  const [view, setView] = useState({ w: 720, h: 460 });
  const [camera, setCamera] = useState<Camera>({ x: 0, y: 0 });

  /**
   * The tiles on screen, which are the stored tiles except while a stroke is in flight.
   *
   * Invariant 3 lives here. A brush drag changes a tile per pointer event and each one is handed
   * back to the parent, which stores it and hands a new array down — but a response landing mid-drag
   * (an autosave, another admin, an undo) must not be adopted, or the tile under the cursor is
   * pulled out from under the brush. The guard is inside this component because the stroke is.
   */
  const [shown, setShown] = useState(tiles);
  useEffect(() => {
    if (strokeRef.current) return;
    setShown(tiles);
  }, [tiles]);

  /**
   * Show it, then store it — in that order, and never from inside a state updater (invariant 2).
   *
   * Showing first is what keeps the brush under the cursor: storing goes through the parent and, for
   * the admin, through a full revalidation of the curriculum, and waiting for that to come back
   * would make a drag lag a frame or two behind the finger.
   */
  const commit = useCallback(
    (next: Uint8Array, coalesce: boolean) => {
      setShown(next);
      onPaint(next, coalesce);
    },
    [onPaint],
  );

  const paintedBiome = useMemo(() => biomeFor(biome), [biome]);

  /* ---------------------------------------------------------------- the window */

  useEffect(() => {
    // The CANVAS, not a wrapper: a wrapper carrying a border is a few pixels wider than the canvas
    // inside it, and sizing the backing store from it would stretch the blit off whole device
    // pixels and put the hit-test a fraction of a tile from the drawing.
    const element = canvasRef.current;
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      const next = {
        w: Math.max(160, Math.round(rect.width)),
        h: Math.max(120, Math.round(rect.height)),
      };
      setView((current) => (current.w === next.w && current.h === next.h ? current : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const geometry = useMemo(
    () => ({ pixelW: width * TILE, pixelH: height * TILE, viewW: view.w, viewH: view.h, zoom }),
    [height, view.h, view.w, width, zoom],
  );

  /**
   * Keep the camera inside the island whenever the island, the window or the zoom changes.
   *
   * Drawing clamps on its own, but the stored camera has to be clamped too: a value left outside the
   * bounds swallows the first several tiles of every pan, because each small delta still lands
   * outside and still clamps to the same edge.
   */
  useEffect(() => {
    setCamera((current) => {
      const next = clampCamera({ ...geometry, camera: current });
      return next.x === current.x && next.y === current.y ? current : next;
    });
  }, [geometry]);

  const tileFromEvent = useCallback(
    (event: { clientX: number; clientY: number }): Tile | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const transform = viewTransform({
        pixelW: width * TILE,
        pixelH: height * TILE,
        viewW: rect.width,
        viewH: rect.height,
        zoom,
        camera,
      });
      return tileAtPointer(
        transform,
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        TILE,
        width,
        height,
      );
    },
    [camera, height, width, zoom],
  );

  /* ---------------------------------------------------------------- strokes */

  /**
   * Abandon the stroke in progress.
   *
   * Clean precisely because of coalescing: however many tiles a drag has painted, it is one entry on
   * the undo stack. This is what makes "a second finger cancels whatever the first one was doing" a
   * safe rule rather than a lossy one.
   */
  const abortStroke = useCallback(() => {
    const stroke = strokeRef.current;
    strokeRef.current = null;
    onStroke?.(false);
    if (!stroke) return;
    // Every mode takes its entry BACK rather than writing another one. Committing the pre-stroke
    // tiles instead would leave the cancelled rectangle one press of Poništi away and destroy the
    // redo branch on the way — an abort that has to be undone is not an abort.
    if (stroke.mode === 'rect') onUndo();
    else if (stroke.mode === 'paint' && stroke.committed) onUndo();
    else if (stroke.mode === 'drag' && stroke.moved) onUndo();
  }, [commit, onStroke, onUndo]);

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    // Middle button is the camera and never the drawing, on every canvas anybody has used.
    if (event.button === 1) {
      event.preventDefault();
      panning.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* the pan works without it */
      }
      return;
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    /**
     * Two fingers are always the camera.
     *
     * One finger paints, two pan and zoom — the iPad-painting convention, chosen over the
     * timing-based alternative (a pointer that moves far enough fast enough becomes a pan) because
     * that one has to guess whether somebody meant to draw, and it guesses wrong often enough to be
     * maddening. This rule needs no threshold and no timer and cannot be ambiguous.
     */
    if (pointers.current.size === 2) {
      abortStroke();
      const [a, b] = [...pointers.current.values()];
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect && a && b) {
        const mid = { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top };
        pinch.current = {
          startDistance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
          startZoom: zoom,
          held: bufferPointAt(
            viewTransform({ ...geometry, viewW: rect.width, viewH: rect.height, camera }),
            mid,
          ),
        };
      }
      return;
    }
    // A third finger is not a gesture. Ignoring it beats reinterpreting the two already down.
    if (pointers.current.size > 2) return;

    const at = tileFromEvent(event);
    if (!at) return;
    // Capture keeps a stroke alive when the cursor leaves the canvas mid-drag. It throws if the
    // pointer is already gone, and letting that abort the handler would lose the whole stroke over
    // the one part of it that does not matter.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* the stroke works without it */
    }

    if (tool === 'kapaljka') {
      onSample((shown[at.y * width + at.x] ?? Terrain.Sea) as Terrain);
      return;
    }

    if (tool === 'gumica') {
      const hit = objects.hitTest(at.x, at.y);
      // No confirmation. Undo is the safety net and it already exists; a dialog per barrel is the
      // friction that made removing things feel like a decision.
      if (hit !== null) objects.remove(hit);
      else onPick(null);
      return;
    }

    if (tool === 'objekti') {
      if (objects.armed) {
        objects.place(at.x, at.y);
        return;
      }
      const hit = objects.hitTest(at.x, at.y);
      onPick(hit !== null ? at : null);
      if (hit !== null) {
        strokeRef.current = { mode: 'drag', target: hit, last: at, moved: false };
        onStroke?.(true);
      }
      return;
    }

    if (tool === 'kanta') {
      const next = fillFrom({ width, height }, at.x, at.y, terrain, shown);
      if (next) commit(next, false);
      return;
    }

    if (tool === 'pravougaonik') {
      strokeRef.current = { mode: 'rect', from: at, to: at, base: shown };
      onStroke?.(true);
      commit(rectBetween({ width, height }, at, at, terrain, shown), false);
      return;
    }

    // Invariant 1: the stroke carries its own buffer.
    const stroke: StrokeState<Ref> = { mode: 'paint', committed: false, last: at, tiles: shown };
    strokeRef.current = stroke;
    onStroke?.(true);
    const next = paintLine({ width, height }, at, at, terrain, brush, shown);
    if (next) {
      stroke.tiles = next;
      stroke.committed = true;
      commit(next, false);
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const pan = panning.current;
    if (pan && pan.id === event.pointerId) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCamera(
        clampCamera({
          ...geometry,
          viewW: rect.width,
          viewH: rect.height,
          camera: {
            x: camera.x - (event.clientX - pan.x) / zoom,
            y: camera.y - (event.clientY - pan.y) / zoom,
          },
        }),
      );
      panning.current = { id: pan.id, x: event.clientX, y: event.clientY };
      return;
    }
    if (!strokeRef.current && !pinch.current) onHover(tileFromEvent(event));
    if (pointers.current.has(event.pointerId))
      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const gesture = pinch.current;
    if (gesture && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect || !a || !b) return;
      const mid = { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top };
      const spread = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)) / gesture.startDistance;
      const target = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(gesture.startZoom * spread)));
      const base = { ...geometry, viewW: rect.width, viewH: rect.height, zoom: target };
      setCamera(cameraPutting(base, gesture.held, mid));
      if (target !== zoom) onZoom(target);
      return;
    }

    const stroke = strokeRef.current;
    if (!stroke) return;
    const at = tileFromEvent(event);
    if (!at) return;

    if (stroke.mode === 'drag') {
      if (stroke.last.x === at.x && stroke.last.y === at.y) return;
      stroke.last = at;
      // Coalesced after the first move: one drag is one thing to undo, however far it went.
      objects.move(stroke.target, at.x, at.y, stroke.moved);
      onPick(at);
      stroke.moved = true;
      return;
    }

    if (stroke.mode === 'rect') {
      if (stroke.to.x === at.x && stroke.to.y === at.y) return;
      stroke.to = at;
      commit(rectBetween({ width, height }, stroke.from, at, terrain, stroke.base), true);
      return;
    }

    if (stroke.last.x === at.x && stroke.last.y === at.y) return;
    const from = stroke.last;
    stroke.last = at;
    // Invariant 2: computed first, published after — never inside a state updater.
    const next = paintLine({ width, height }, from, at, terrain, brush, stroke.tiles);
    if (!next) return;
    stroke.tiles = next;
    commit(next, stroke.committed);
    stroke.committed = true;
  };

  /**
   * The camera, for everything that is not two fingers.
   *
   * Two-finger pan covers a touch screen and nothing else — and this canvas replaced a scroll
   * container, which is what used to make a large island reachable on a desktop. Without this, a
   * mouse is locked to the top-left corner of anything bigger than the window. Wheel and trackpad
   * scroll pan; with a modifier they zoom, which is the convention every map on the web uses.
   */
  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const base = { ...geometry, viewW: rect.width, viewH: rect.height };

    if (event.ctrlKey || event.metaKey) {
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom + (event.deltaY < 0 ? 1 : -1)));
      if (next === zoom) return;
      const focus = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const held = bufferPointAt(viewTransform({ ...base, camera }), focus);
      setCamera(cameraPutting({ ...base, zoom: next }, held, focus));
      onZoom(next);
      return;
    }

    setCamera(
      clampCamera({
        ...base,
        camera: { x: camera.x + event.deltaX / zoom, y: camera.y + event.deltaY / zoom },
      }),
    );
  };

  /** Middle-button drag, the other thing every canvas on a desktop does. */
  const panning = useRef<{ id: number; x: number; y: number } | null>(null);

  const endStroke = (event?: React.PointerEvent<HTMLCanvasElement>) => {
    if (event && panning.current?.id === event.pointerId) {
      panning.current = null;
      return;
    }
    if (event) pointers.current.delete(event.pointerId);
    // The pinch ends when it stops being two fingers, not when the last one lifts: a finger left on
    // the glass after a pinch must not start painting halfway through the gesture.
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size > 0 && pinch.current) return;

    const stroke = strokeRef.current;
    strokeRef.current = null;
    onStroke?.(false);
    if (stroke?.mode === 'rect')
      commit(rectBetween({ width, height }, stroke.from, stroke.to, terrain, stroke.base), false);
  };

  /* ---------------------------------------------------------------- drawing */

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const pixelW = width * TILE;
    const pixelH = height * TILE;

    let buffer = bufferRef.current;
    if (!buffer) {
      buffer = document.createElement('canvas');
      bufferRef.current = buffer;
    }
    if (buffer.width !== pixelW || buffer.height !== pixelH) {
      buffer.width = pixelW;
      buffer.height = pixelH;
    }
    const ctx = buffer.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;

    // The ground is expensive (thousands of fillRects) and only changes when a tile does, so it is
    // cached against a fingerprint of the tiles rather than repainted for every hover.
    const key = `${width}x${height}:${biome ?? 'auto'}:${fingerprint(shown)}`;
    if (groundRef.current?.key !== key) {
      const shored = applyShoreline(shown, width, height);
      // Painted back into the same surface: a brush drag repaints this once per tile, and a fresh
      // canvas each time is megabytes of allocation churn under the cursor.
      groundRef.current = {
        key,
        canvas: renderGroundLayer(
          previewWorld(shored, width, height),
          () => paintedBiome,
          groundRef.current?.canvas,
        ),
      };
    }
    ctx.drawImage(groundRef.current.canvas, 0, 0);

    if (grid) {
      ctx.fillStyle = 'rgba(11,39,64,0.16)';
      for (let x = 1; x < width; x++) ctx.fillRect(x * TILE, 0, 1, pixelH);
      for (let y = 1; y < height; y++) ctx.fillRect(0, y * TILE, pixelW, 1);
    }

    overlay(ctx);

    // The backing store is the window's size, capped — never the island's — which is what keeps a
    // large island at high zoom from asking for a canvas the device will refuse to allocate.
    const dpr = backingScale(view.w, view.h, window.devicePixelRatio || 1);
    const backingW = Math.max(1, Math.round(view.w * dpr));
    const backingH = Math.max(1, Math.round(view.h * dpr));
    if (canvas.width !== backingW || canvas.height !== backingH) {
      canvas.width = backingW;
      canvas.height = backingH;
    }

    const transform = viewTransform({ pixelW, pixelH, viewW: view.w, viewH: view.h, zoom, camera });
    const out = canvas.getContext('2d')!;
    out.imageSmoothingEnabled = false;
    out.clearRect(0, 0, canvas.width, canvas.height);
    out.drawImage(
      buffer,
      transform.camX,
      transform.camY,
      transform.sw,
      transform.sh,
      Math.round(transform.offsetX * dpr),
      Math.round(transform.offsetY * dpr),
      Math.round(transform.sw * transform.zoom * dpr),
      Math.round(transform.sh * transform.zoom * dpr),
    );
  }, [biome, camera, grid, height, overlay, paintedBiome, shown, view.h, view.w, width, zoom]);

  useEffect(() => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frameRef.current);
  }, [draw]);

  return (
    <div className="wb-view">
      <canvas
        ref={canvasRef}
        className="wb-canvas"
        data-tool={tool}
        aria-label={label}
        role="img"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onWheel={onWheel}
        onAuxClick={(event) => event.preventDefault()}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
        onLostPointerCapture={endStroke}
        onPointerLeave={() => onHover(null)}
      />
    </div>
  );
}
